import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getInvoices } from '../../actions/user.actions';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import { selectUser, selectUserInvoiceHistory } from '../../reducers/user.selectors';
import billingService from '../../services/billing.service';
import useAction from '../../utils/useAction';
import InvoiceHistoryTable from '../InvoiceHistoryTable';
import initFileDownload from '../utils/downloadIFrame';

const InvoiceHistory = () => {
	const { t } = useTranslation();
	const invoiceHistory = useSelector(selectUserInvoiceHistory);
	const getInvoicesAction = useAction(getInvoices);
	const user = useSelector(selectUser);
	const isBusinessUser = !!user?.isSchoolUser || !!user?.isLibraryUser || !!user?.isKeycloakSSO;
	const showInvoiceInformation = !user?.isReadOnlyLicence && !isBusinessUser;

	const [serverError, setServerError] = useState<Error | null>(null);

	const onClickDownload = async (invoiceId: string) => {
		if (!user) return;
		try {
			const invoiceLink = await billingService.getInvoiceDownloadLink(user.id, invoiceId);
			initFileDownload(invoiceLink.requestPath);
		} catch (error: any) {
			setServerError(error);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (user && !invoiceHistory) {
			getInvoicesAction();
		}
	}, [user]);

	if (!showInvoiceInformation) return null;

	return (
		<Box data-testid="invoice-history-section">
			<Typography
				variant="h6"
				color={palette.text.primary}
				sx={{
					paddingY: spaces.md,
				}}
			>
				{t('Invoice history')}
			</Typography>
			<InvoiceHistoryTable
				invoiceHistory={invoiceHistory}
				onClickDownload={onClickDownload}
				pageSize={20}
				serverError={serverError}
			/>
			{serverError && (
				<Typography color="error" sx={{ mt: 2 }}>
					{t('Ups, something went wrong')}
				</Typography>
			)}
		</Box>
	);
};

export default InvoiceHistory;
